/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { buildImageUrl, n, groupVariantsByColor, getVariantColorHex } from "../theme1/utils/theme1Helpers";
import { formatNumber } from "utils/formatNumber";
import Swal from "sweetalert2";

export default function ProductCard({ product, theme, onOpenProduct, onAddToCart }) {
  const [selectedColorHex, setSelectedColorHex] = useState(null);
  const [selectedSizeLabel, setSelectedSizeLabel] = useState(null);

  const colorGroups = useMemo(() => groupVariantsByColor(product?.variants), [product]);
  const uniqueColors = useMemo(() => Object.values(colorGroups), [colorGroups]);
  const displayColors = useMemo(() => uniqueColors.filter((c) => c.hasColor), [uniqueColors]);

  const hasStockInGroup = (group) => {
    if (!group?.variants?.length) return false;
    return group.variants.some(v => {
      const vSizes = v.options || v.sizes || [];
      if (vSizes.length === 0) return n(v.stockQty) > 0;
      const hasSizeStock = vSizes.some(s => n(s.stock || s.stockQty) > 0);
      return hasSizeStock || n(v.stockQty) > 0;
    });
  };

  useEffect(() => {
    if (uniqueColors.length > 0) {
      const firstColor = uniqueColors.find(c => hasStockInGroup(c)) || uniqueColors[0];
      if (!selectedColorHex || !colorGroups[selectedColorHex]) {
        setSelectedColorHex(firstColor.hex);
      }
      const activeGroup = colorGroups[selectedColorHex] || firstColor;
      const allSizes = [];
      activeGroup.variants.forEach(v => {
        const vSizes = v.options || v.sizes || [];
        vSizes.forEach(s => allSizes.push({ label: s.label || s.size, stock: n(s.stock || s.stockQty) }));
      });
      const firstInStock = allSizes.find(s => s.stock > 0) || allSizes[0];
      if (firstInStock && !selectedSizeLabel) {
        setSelectedSizeLabel(firstInStock.label);
      }
    }
  }, [product, uniqueColors, selectedColorHex, colorGroups, selectedSizeLabel]);

  const activeColorGroup = colorGroups[selectedColorHex] || uniqueColors[0];

  const availableSizes = useMemo(() => {
    if (!activeColorGroup) return [];
    const sizeMap = new Map();
    activeColorGroup.variants.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => {
        const label = s.label || s.size;
        let stock = n(s.stock || s.stockQty);
        if (stock <= 0 && n(v.stockQty) > 0) stock = n(v.stockQty);
        if (!sizeMap.has(label)) {
          sizeMap.set(label, { label, stock, variant: v });
        } else {
          const existing = sizeMap.get(label);
          existing.stock += stock;
        }
      });
    });
    return Array.from(sizeMap.values());
  }, [activeColorGroup]);

  const activeSizeObj = useMemo(() => {
    return availableSizes.find(s => s.label === selectedSizeLabel) || availableSizes[0];
  }, [availableSizes, selectedSizeLabel]);

  const currentVariant = activeSizeObj?.variant || activeColorGroup?.variants?.[0];

  // 2. حساب السعر والخصم بناءً على القواعد
  const priceDetails = useMemo(() => {
    if (!currentVariant) return { base: 0, discount: 0, final: 0, percentage: 0 };

    const basePrice = Number(currentVariant.price || 0);
    let discountAmount = Number(currentVariant.discount || 0);

    if (discountAmount === 0 && Number(product.discountValue) > 0) {
      if (Number(product.discountType) === 1) {
        discountAmount = (basePrice * Number(product.discountValue)) / 100;
      } else {
        discountAmount = Number(product.discountValue);
      }
    }

    const finalPrice = basePrice - discountAmount;
    const percentage = Math.round((discountAmount / basePrice) * 100);

    return {
      base: basePrice,
      discount: discountAmount,
      final: Math.max(0, finalPrice),
      percentage
    };
  }, [currentVariant, product.discountValue, product.discountType]);

  const isOutOfStock = useMemo(() => {
    if (availableSizes.length === 0) return Number(currentVariant?.stockQty || 0) <= 0;
    return availableSizes.every(s => s.stock <= 0);
  }, [availableSizes, currentVariant]);

  const isProductOutOfStock = useMemo(() => {
    if (!uniqueColors.length) return Number(currentVariant?.stockQty || 0) <= 0;
    return uniqueColors.every(c => !hasStockInGroup(c));
  }, [uniqueColors, currentVariant]);

  const showOutOfStockAlert = () => {
    Swal.fire({
      icon: "info",
      title: "انتهى من المخزون",
      text: "هذا المنتج غير متوفر حالياً"
    });
  };

  const handleOpenDetails = () => {
    if (isProductOutOfStock) {
      showOutOfStockAlert();
      return;
    }
    onOpenProduct({ productId: product.id, variantId: currentVariant?.variantId });
  };

  const handleQuickAdd = (e) => {
    e.stopPropagation();
    if (isOutOfStock) {
      showOutOfStockAlert();
      return;
    }

    if (!currentVariant || !selectedSizeLabel) {
      Swal.fire({ title: 'تنبيه', text: 'يرجى اختيار اللون والمقاس', icon: 'warning' });
      return;
    }

    // إرسال البيانات بنفس هيكلية الثيم الأول والسيرفر
    onAddToCart({
      ...product,
      itemId: product.id,
      variantId: currentVariant.variantId,
      price: priceDetails.final,
      // أرسلها بـ 3 مسميات مختلفة لقطع الشك باليقين
      color: getVariantColorHex(currentVariant),
      colorHex: getVariantColorHex(currentVariant),
      hex: getVariantColorHex(currentVariant),
      colorName: getVariantColorHex(currentVariant) || "",
      size: selectedSizeLabel,
      itemName: product.name,
      stockQty: activeSizeObj ? activeSizeObj.stock : Number(currentVariant.stockQty || 0),
      imageUrl: currentVariant?.imageUrl || product.mainImageUrl || product.mainImage,
    }, 1);
  };

  const handleColorSelect = (hex) => {
    setSelectedColorHex(hex);
    const group = colorGroups[hex];
    const allSizes = [];
    group?.variants?.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => allSizes.push({ label: s.label || s.size, stock: n(s.stock || s.stockQty) }));
    });
    const firstInStock = allSizes.find(s => s.stock > 0) || allSizes[0];
    setSelectedSizeLabel(firstInStock?.label || null);
  };

  return (
    <>
      <div
        className={`bg-white rounded-[2.5rem] overflow-hidden border flex flex-col group cursor-pointer transition-all duration-500 ${isProductOutOfStock
          ? 'border-gray-200 opacity-80'
          : 'border-gray-100 hover:shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:-translate-y-1'
          }`}
        onClick={handleOpenDetails}
      >
        {/* Image Section */}
        <div className="h-64 bg-[#F8F9FB] relative flex items-center justify-center p-6 overflow-hidden">
          <img
            src={buildImageUrl(currentVariant?.imageUrl || product.mainImageUrl || product.mainImage)}
            className="max-w-full max-h-full object-contain mix-blend-multiply transition-transform duration-700 group-hover:scale-110"
            alt={product.name}
          />

          {priceDetails.discount > 0 && (
            <div className="absolute top-4 right-4 bg-[#FF4D4D] text-white text-[11px] px-3 py-1 rounded-full font-bold shadow-lg shadow-red-200 z-10">
              خصم {priceDetails.percentage}%
            </div>
          )}



          {isProductOutOfStock && (
            <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px] flex items-center justify-center z-20">
              <span className="bg-white text-gray-800 text-[11px] px-4 py-2 rounded-full font-bold shadow-sm uppercase tracking-wider">نفذت الكمية</span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-6 flex flex-col gap-4">
          {/* Tags */}
          {product.tags && product.tags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 -mb-2">
              {product.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 rounded-full text-[9px] font-bold"
                  style={{ background: `${theme.primary}12`, color: theme.primary, border: `1px solid ${theme.primary}22` }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {(product.category || product.subCategory) && (
            <div className="flex flex-col items-center gap-0.5 -mt-2">
              {product.category && (
                <span className="text-[10px] font-black uppercase" style={{ color: theme.primary }}>
                  {product.category}
                </span>
              )}
              {product.subCategory && (
                <span className="text-[9px] font-bold text-gray-400 uppercase">
                  {product.subCategory}
                </span>
              )}
            </div>
          )}

          <h3 className="font-bold text-gray-900 text-lg text-center line-clamp-1">{product.name}</h3>

          {/* Color Circles */}
          {displayColors.length > 0 && (
            <div className="flex justify-center gap-2 flex-wrap">
              {displayColors.map((c) => (
                <button
                  key={c.hex}
                  onClick={(e) => { e.stopPropagation(); handleColorSelect(c.hex); }}
                  className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${selectedColorHex === c.hex ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          )}

          {/* Size Pills */}
          {availableSizes.length > 0 && (
            <div className="flex justify-center gap-1 flex-wrap">
              {availableSizes.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (opt.stock <= 0) {
                      showOutOfStockAlert();
                      return;
                    }
                    setSelectedSizeLabel(opt.label);
                  }}
                  className={`px-3 py-1 rounded-lg text-[10px] font-bold border transition-all
                    ${selectedSizeLabel === opt.label ? 'bg-gray-800 text-white border-gray-800' : 'bg-gray-50 text-gray-400 border-gray-100 hover:bg-gray-100'}
                    ${opt.stock <= 0 ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* Pricing */}
          <div className="flex flex-col items-center gap-1 min-h-[50px]">
            <div className="flex items-center gap-2">
              <span className={`${theme.text} font-black text-xl`}>{formatNumber(priceDetails.final)}</span>
              <span className="text-gray-400 text-xs font-bold mt-1">د.ع</span>
            </div>
            {priceDetails.discount > 0 && (
              <span className="text-gray-300 line-through text-sm">{formatNumber(priceDetails.base)} د.ع</span>
            )}
          </div>

          {/* Add to Cart Button */}
          <button
            disabled={isOutOfStock}
            className={`w-full py-4 rounded-2xl font-bold text-sm mt-2 transition-all duration-300 active:scale-95 flex items-center justify-center gap-2
              ${isOutOfStock ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : `${theme.bg} text-white hover:shadow-xl hover:shadow-indigo-100`}
            `}
            onClick={handleQuickAdd}
          >
            {isOutOfStock ? "غير متوفر" : "أضف للسلة"}
          </button>
        </div>
      </div>
    </>
  );
}
